import React from "react"
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material"
import VerifiedIcon from "@mui/icons-material/Verified"
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder"
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"
import RepeatIcon from "@mui/icons-material/Repeat"
import FormatQuoteIcon from "@mui/icons-material/FormatQuote"

type Props = {
  threadslResults: any[]
}

export default function ThreadResultsViewer({ threadslResults }: Props) {
  const [currentTab, setCurrentTab] = React.useState(0)

  const result = threadslResults?.[0]

  if (!result?.data?.length) {
    return null
  }

  const profile = result.data.find((item: any) => item.type === "ProfileData")
  const posts = result.data.filter((item: any) => item.type === "PostItem")

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  const formatNumber = (value?: number | null) => {
    if (value === null || value === undefined) return "—"
    return value.toLocaleString()
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          aria-label="threads result tabs"
          sx={{
            px: 3,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tab
            label="Profile"
            id="simple-tab-0"
            aria-controls="simple-tabpanel-0"
          />

          {posts.length > 0 && (
            <Tab
              label={`Posts (${posts.length})`}
              id="simple-tab-1"
              aria-controls="simple-tabpanel-1"
            />
          )}
        </Tabs>

        <Box
          role="tabpanel"
          hidden={currentTab !== 0}
          id="simple-tabpanel-0"
          aria-labelledby="simple-tab-0"
        >
          {currentTab === 0 && profile && (
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Avatar
                    src={profile.profileImageUrl}
                    alt={profile.profileName}
                    sx={{ width: 76, height: 76 }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" fontWeight={700} noWrap>
                        {profile.profileName}
                      </Typography>

                      {profile.is_verified && (
                        <VerifiedIcon color="primary" fontSize="small" />
                      )}
                    </Stack>

                    <Typography variant="body2" color="text.secondary">
                      @{profile.profile_id}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={3}
                      mt={2}
                      flexWrap="wrap"
                      sx={{
                        gap: 1.5,
                      }}
                    >
                      <Typography variant="body2">
                        <Box component="span" fontWeight={700}>
                          {formatNumber(profile.profileData?.followers_count)}
                        </Box>{" "}
                        followers
                      </Typography>

                      <Typography variant="body2">
                        <Box component="span" fontWeight={700}>
                          {formatNumber(profile.profileData?.following_count)}
                        </Box>{" "}
                        following
                      </Typography>

                      <Typography variant="body2">
                        <Box component="span" fontWeight={700}>
                          {formatNumber(profile.profileData?.posts_count)}
                        </Box>{" "}
                        posts
                      </Typography>

                      <Typography variant="body2">
                        <Box component="span" fontWeight={700}>
                          {formatNumber(profile.profileData?.views_count)}
                        </Box>{" "}
                        views
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>

                {profile.biography && (
                  <Typography
                    sx={{
                      lineHeight: 1.7,
                      textAlign: "left",
                    }}
                  >
                    {profile.biography}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </Box>

        {posts.length > 0 && (
          <Box
            role="tabpanel"
            hidden={currentTab !== 1}
            id="simple-tabpanel-1"
            aria-labelledby="simple-tab-1"
          >
            {currentTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {posts.map((post: any) => (
                    <Card
                      key={post.post_id}
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        transition: "all .2s ease",
                        "&:hover": {
                          boxShadow: 2,
                        },
                      }}
                    >
                      <CardContent>
                        <Stack spacing={2.5}>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                          >
                            <Avatar
                              src={post.original_post_user_image}
                              alt={post.original_post_user_name}
                              sx={{
                                mt: 0.5,
                                ml: 1,
                              }}
                            />

                            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                              <Typography fontWeight={700} noWrap>
                                {post.original_post_user_name}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {post.created_date}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Typography
                            whiteSpace="pre-wrap"
                            sx={{
                              lineHeight: 1.8,
                              textAlign: "left",
                              fontSize: "0.95rem",
                              pl: 1.5,
                            }}
                          >
                            {post.text}
                          </Typography>

                          {!!post.images_url?.length || !!post.videos_url?.length ? (
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{
                                pl: 1.5,
                                overflowX: "auto",
                                pb: 1,
                              }}
                            >
                              {post.images_url?.map((url: string, index: number) => (
                                <Box
                                  key={`img-${index}`}
                                  component="img"
                                  src={url}
                                  alt={`Post image ${index + 1}`}
                                  sx={{
                                    width: 250,
                                    height: 250,
                                    flexShrink: 0,
                                    objectFit: "cover",
                                    borderRadius: 2,
                                    border: 1,
                                    borderColor: "divider",
                                  }}
                                />
                              ))}

                              {post.videos_url?.map((url: string, index: number) => (
                                <Box
                                  key={`video-${index}`}
                                  component="video"
                                  controls
                                  preload="metadata"
                                  sx={{
                                    width: 250,
                                    height: 250,
                                    flexShrink: 0,
                                    objectFit: "cover",
                                    borderRadius: 2,
                                    border: 1,
                                    borderColor: "divider",
                                  }}
                                >
                                  <source src={url} type="video/mp4" />
                                </Box>
                              ))}
                            </Stack>
                          ): null}

                          {!!post.external_link_url?.length && (
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: "action.hover",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  mb: 1,
                                  fontWeight: 600,
                                }}
                              >
                                External links:
                              </Typography>

                              <Stack spacing={1}>
                                {post.external_link_url.map((url: string) => (
                                  <Link
                                    key={url}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    sx={{
                                      wordBreak: "break-all",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {url}
                                  </Link>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          <Divider />
                          <Stack
                            direction="row"
                            spacing={3}
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{
                              color: "text.secondary",
                              gap: 1,
                            }}
                          >
                            {[
                              {
                                icon: <FavoriteBorderIcon fontSize="small" />,
                                value: post.like_count,
                              },
                              {
                                icon: (
                                  <ChatBubbleOutlineIcon fontSize="small" />
                                ),
                                value: post.comments_count,
                              },
                              {
                                icon: <RepeatIcon fontSize="small" />,
                                value: post.reposts_count,
                              },
                              {
                                icon: <FormatQuoteIcon fontSize="small" />,
                                value: post.quotes_count,
                              },
                            ].map((item, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  py: 0.5,
                                  px: 0.75,
                                  borderRadius: 1,
                                  cursor: "pointer",
                                  transition: "all .2s ease",
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                    color: "text.primary",
                                  },
                                }}
                              >
                                {item.icon}

                                <Typography variant="body2">
                                  {item.value ?? 0}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>

                          {post.post_url && (
                            <Link
                              href={post.post_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{
                                fontWeight: 500,
                                alignSelf: "flex-start",
                                pl: 1.5,
                              }}
                            >
                              View on Threads →
                            </Link>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}